import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputObservable, connectState } from '@y42/angular/util';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { getGojsVariableColor } from '@y42/model';
import { Diagram, Overview, Shape } from 'gojs';
import { Observable, Subject, debounceTime, map, tap } from 'rxjs';
import { GoJsOverviewPosition, GoJsOverviewPositionToClass, gojsOverview } from './gojs-overview';

@UntilDestroy()
@Component({
  selector: 'y42-gojs-overview',
  templateUrl: './gojs-overview.component.html',
  styleUrls: ['./gojs-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsOverviewComponent implements OnInit, OnDestroy {
  constructor(private readonly userColorSchemeService: UserColorSchemeService) {
    this.setOverviewRectangleOnDarkModeChange().pipe(untilDestroyed(this)).subscribe();
  }

  @Input()
  @InputObservable()
  diagram!: Diagram;
  diagram$!: Observable<Diagram>;

  @Input()
  isOverviewActive = false;

  @Input()
  @InputObservable()
  position = GoJsOverviewPosition.RightWithSidebar;
  position$!: Observable<GoJsOverviewPosition>;

  @ViewChild('overviewRef', { static: true }) private readonly overviewRef!: ElementRef;
  @ViewChild('zoomRangeInputRef') zoomRangeInputRef!: ElementRef;

  private overview?: Overview;
  private _initialScale = 0;
  private readonly viewportBoundsChanged$ = new Subject<void>();
  private readonly viewportBoundsChangedWithDebounce$ = this.viewportBoundsChanged$.pipe(debounceTime(500));

  readonly state = connectState(this, {
    diagramScale: this.viewportBoundsChangedWithDebounce$.pipe(map(() => `${Math.round(this.diagram.scale * 100)}%`)),
    isFitToScreenDisabled: this.viewportBoundsChangedWithDebounce$.pipe(map(() => this.diagram.scale === 1)),
    positionClass: this.position$.pipe(map((position) => GoJsOverviewPositionToClass[position])),
  });

  ngOnInit(): void {
    this.initializeOverview();
    // Set up diagram listener so the slider can be kept in sync with the diagram's scale
    this.diagram.addDiagramListener('ViewportBoundsChanged', this.viewportBoundsChangedPusher.bind(this));

    this.scaleToValueOnViewportBoundsChanged().subscribe();
  }

  ngOnDestroy(): void {
    this.diagram.removeDiagramListener('ViewportBoundsChanged', this.viewportBoundsChangedPusher.bind(this));
  }

  toggleOverview(): void {
    this.isOverviewActive = !this.isOverviewActive;
  }

  fitToScreen(): void {
    this.diagram.commandHandler.zoomToFit();
  }

  zoomOut(): void {
    const _zoomRangeInput = this.zoomRangeInputRef.nativeElement as HTMLInputElement;
    _zoomRangeInput.stepDown();
    this.valueToScale();
  }

  zoomIn(): void {
    const _zoomRangeInput = this.zoomRangeInputRef.nativeElement as HTMLInputElement;
    _zoomRangeInput.stepUp();
    this.valueToScale();
  }

  onRangeChanged(): void {
    this.valueToScale();
  }

  private initializeOverview(): void {
    this.overview = gojsOverview(this.diagram);
    this.overview.div = this.overviewRef.nativeElement;
    this._initialScale = this.diagram.scale;
  }

  private viewportBoundsChangedPusher() {
    this.viewportBoundsChanged$.next();
  }

  private scaleToValueOnViewportBoundsChanged() {
    return this.viewportBoundsChangedWithDebounce$.pipe(
      tap(() => this.scaleToValue()),
      untilDestroyed(this),
    );
  }

  /**
   * Update the value of the slider input to match the diagram's scale.
   */
  private scaleToValue() {
    const _zoomRangeInput = this.zoomRangeInputRef.nativeElement as HTMLInputElement;
    const diagram = this.diagram;
    const A = this._initialScale;
    const B = diagram.commandHandler.zoomFactor;
    const y1 = diagram.scale;
    if (_zoomRangeInput) {
      _zoomRangeInput.value = Math.round(Math.log(y1 / A) / Math.log(B)).toString();
    }
  }

  /**
   * Update the diagram's scale to match the value of the slider input.
   */
  private valueToScale() {
    const _zoomRangeInput = this.zoomRangeInputRef.nativeElement as HTMLInputElement;
    const diagram = this.diagram;
    const x = parseFloat(_zoomRangeInput.value);
    const A = this._initialScale;
    const B = diagram.commandHandler.zoomFactor;
    diagram.scale = A * Math.pow(B, x);
  }

  private setOverviewRectangleOnDarkModeChange() {
    return this.userColorSchemeService.hasDarkMode$.pipe(
      tap((hasDarkMode) => {
        if (!this.overview) {
          return;
        }
        (this.overview.box.elt(0) as Shape).stroke = getGojsVariableColor('gray300', hasDarkMode);
      }),
    );
  }
}
